// src/components/learning/islamic/SuratesHub.js
// Interface ludique pour apprendre les sourates courtes du Coran
// Avec intégration API Quran.com pour audio et mode mémorisation

import React, { useState, useEffect, useRef } from 'react';
import { suratesData, surateEncouragements, surateBadges } from './suratesData';
import Confetti from '../Confetti';

// Configuration des récitateurs disponibles
const RECITERS = [
  { id: 7, name: "Mishary Rashid Alafasy", nameAr: "مشاري العفاسي" },
  { id: 2, name: "Abdul Basit (Murattal)", nameAr: "عبد الباسط" },
  { id: 1, name: "Abdul Basit (Mujawwad)", nameAr: "عبد الباسط مجود" },
  { id: 3, name: "Abdur-Rahman as-Sudais", nameAr: "عبدالرحمن السديس" },
  { id: 4, name: "Abu Bakr al-Shatri", nameAr: "أبو بكر الشاطري" },
  { id: 5, name: "Hani ar-Rifai", nameAr: "هاني الرفاعي" },
  { id: 12, name: "Mahmoud Khalil Al-Husary", nameAr: "محمود خليل الحصري" },
  { id: 6, name: "Mahmoud Khalil Al-Husary (Muallim)", nameAr: "الحصري معلم" },
];

const SuratesHub = ({ onClose }) => {
  const [selectedSurate, setSelectedSurate] = useState(null);
  const [viewMode, setViewMode] = useState('read'); // 'read', 'memorize', 'quiz'
  const [showTranslation, setShowTranslation] = useState(true);
  const [showTransliteration, setShowTransliteration] = useState(true);
  
  // Audio state
  const [selectedReciter, setSelectedReciter] = useState(RECITERS[0]);
  const [audioUrls, setAudioUrls] = useState([]);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [currentVerseIndex, setCurrentVerseIndex] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const audioRef = useRef(null);
  
  // Mode mémorisation
  const [memorizeSettings, setMemorizeSettings] = useState({
    startVerse: 0,
    endVerse: 0,
    repeatCount: 3,
    currentRepeat: 0,
    pauseBetween: 1500 // ms
  });
  const [isMemorizing, setIsMemorizing] = useState(false);
  
  // Quiz state
  const [quizState, setQuizState] = useState({
    currentQuestion: 0,
    score: 0,
    showResult: false
  });
  const [feedback, setFeedback] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);

  // Charger les URLs audio depuis l'API Quran.com
  const loadAudioUrls = async (surate) => {
    setIsLoadingAudio(true);
    try {
      const response = await fetch(
        `https://api.quran.com/api/v4/recitations/${selectedReciter.id}/by_chapter/${surate.number}`
      );
      const data = await response.json();
      
      if (data.audio_files) {
        // Construire les URLs complètes
        const urls = data.audio_files.map(file => ({
          verseKey: file.verse_key,
          url: file.url.startsWith('http') 
            ? file.url 
            : `https://verses.quran.com/${file.url}`
        }));
        setAudioUrls(urls);
      }
    } catch (error) {
      console.error('Erreur chargement audio:', error);
      // Fallback: utiliser Web Speech API
      setAudioUrls([]);
    }
    setIsLoadingAudio(false);
  };

  // Charger audio quand on sélectionne une sourate ou change de récitateur
  useEffect(() => {
    if (selectedSurate) {
      loadAudioUrls(selectedSurate);
      setMemorizeSettings(prev => ({
        ...prev,
        endVerse: selectedSurate.verses.length - 1
      }));
    }
  }, [selectedSurate, selectedReciter]);

  // Jouer un verset spécifique
  const playVerse = (index) => {
    if (audioUrls.length > 0 && audioUrls[index]) {
      setCurrentVerseIndex(index);
      if (audioRef.current) {
        audioRef.current.src = audioUrls[index].url;
        audioRef.current.playbackRate = playbackSpeed;
        audioRef.current.play();
        setIsPlaying(true);
      }
    } else {
      // Fallback Web Speech API
      const verse = selectedSurate.verses[index];
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(verse.arabic);
        utterance.lang = 'ar-SA';
        utterance.rate = 0.6 * playbackSpeed;
        utterance.onend = () => handleVerseEnd();
        window.speechSynthesis.speak(utterance);
        setCurrentVerseIndex(index);
        setIsPlaying(true);
      }
    }
  };

  // Jouer toute la sourate
  const playSurate = () => {
    setCurrentVerseIndex(0);
    playVerse(0);
  };

  // Arrêter la lecture
  const stopPlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    window.speechSynthesis?.cancel();
    setIsPlaying(false);
    setCurrentVerseIndex(-1);
    setIsMemorizing(false);
  };

  // Gestion fin de verset
  const handleVerseEnd = () => {
    if (isMemorizing) {
      handleMemorizeNext();
    } else if (currentVerseIndex < selectedSurate.verses.length - 1) {
      // Passer au verset suivant
      setTimeout(() => {
        playVerse(currentVerseIndex + 1);
      }, 500);
    } else {
      setIsPlaying(false);
      setCurrentVerseIndex(-1);
    }
  };

  // Mode mémorisation - logique de répétition
  const handleMemorizeNext = () => {
    const { startVerse, endVerse, repeatCount, currentRepeat, pauseBetween } = memorizeSettings;
    
    if (currentVerseIndex < endVerse) {
      // Passer au verset suivant dans la plage
      setTimeout(() => {
        playVerse(currentVerseIndex + 1);
      }, pauseBetween);
    } else if (currentRepeat < repeatCount - 1) {
      // Recommencer la plage
      setMemorizeSettings(prev => ({
        ...prev,
        currentRepeat: prev.currentRepeat + 1
      }));
      setTimeout(() => {
        playVerse(startVerse);
      }, pauseBetween * 2);
    } else {
      // Fin de la mémorisation
      setIsMemorizing(false);
      setIsPlaying(false);
      setCurrentVerseIndex(-1);
      setMemorizeSettings(prev => ({ ...prev, currentRepeat: 0 }));
    }
  };

  // Démarrer la mémorisation
  const startMemorization = () => {
    setIsMemorizing(true);
    setMemorizeSettings(prev => ({ ...prev, currentRepeat: 0 }));
    playVerse(memorizeSettings.startVerse);
  };

  // Audio element event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.onended = handleVerseEnd;
      audio.onplay = () => setIsPlaying(true);
      audio.onpause = () => setIsPlaying(false);
    }
  }, [currentVerseIndex, isMemorizing, memorizeSettings]);

  // Quiz handlers
  const handleQuizAnswer = (answer) => {
    const currentQ = selectedSurate.quiz[quizState.currentQuestion];
    const isCorrect = answer === currentQ.correct;
    
    if (isCorrect) {
      setFeedback({ type: 'correct', message: surateEncouragements.correct[Math.floor(Math.random() * surateEncouragements.correct.length)] });
    } else {
      setFeedback({ type: 'wrong', message: surateEncouragements.wrong[Math.floor(Math.random() * surateEncouragements.wrong.length)] });
    }

    setTimeout(() => {
      setFeedback(null);
      const newState = {
        ...quizState,
        score: isCorrect ? quizState.score + 1 : quizState.score,
        currentQuestion: quizState.currentQuestion + 1
      };

      if (newState.currentQuestion >= selectedSurate.quiz.length) {
        newState.showResult = true;
        if (newState.score === selectedSurate.quiz.length) {
          setShowConfetti(true);
        }
      }

      setQuizState(newState);
    }, 1500);
  };

  const resetQuiz = () => {
    setQuizState({ currentQuestion: 0, score: 0, showResult: false });
    setShowConfetti(false);
  };

  // ==================== VUE DÉTAIL SOURATE ====================
  if (selectedSurate) {
    
    // Mode Quiz
    if (viewMode === 'quiz') {
      if (quizState.showResult) {
        const perfect = quizState.score === selectedSurate.quiz.length;
        return (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}>
            {showConfetti && <Confetti type="gold" duration={4000} />}
            
            <div style={{
              background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
              borderRadius: '24px',
              padding: '3rem',
              textAlign: 'center',
              maxWidth: '500px'
            }}>
              <div style={{ fontSize: '5rem', marginBottom: '1rem' }}>
                {perfect ? '🏆' : '⭐'}
              </div>
              <h2 style={{ color: 'white', margin: '0 0 1rem 0' }}>
                {perfect ? 'Parfait !' : 'Bien joué !'}
              </h2>
              <div style={{
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '2rem'
              }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#10b981' }}>
                  {quizState.score}/{selectedSurate.quiz.length}
                </div>
              </div>
              
              {perfect && (
                <div style={{
                  background: selectedSurate.gradient,
                  borderRadius: '12px',
                  padding: '1rem',
                  marginBottom: '2rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '1rem'
                }}>
                  <span style={{ fontSize: '2rem' }}>{surateBadges[selectedSurate.name]?.icon}</span>
                  <div style={{ color: 'white', fontWeight: 'bold' }}>
                    Badge "{surateBadges[selectedSurate.name]?.name}" débloqué !
                  </div>
                </div>
              )}
              
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <button onClick={resetQuiz} style={{ padding: '1rem 2rem', borderRadius: '12px', border: 'none', background: selectedSurate.gradient, color: 'white', fontWeight: 'bold', cursor: 'pointer' }}>
                  🔄 Rejouer
                </button>
                <button onClick={() => { setViewMode('read'); resetQuiz(); }} style={{ padding: '1rem 2rem', borderRadius: '12px', border: '2px solid rgba(255,255,255,0.3)', background: 'transparent', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}>
                  ← Retour
                </button>
              </div>
            </div>
          </div>
        );
      }

      const currentQ = selectedSurate.quiz[quizState.currentQuestion];
      
      return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.95)', display: 'flex', flexDirection: 'column', zIndex: 9999 }}>
          <div style={{ padding: '1rem 1.5rem', background: selectedSurate.gradient, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <button onClick={() => { setViewMode('read'); resetQuiz(); }} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', padding: '0.5rem 1rem', color: 'white', cursor: 'pointer' }}>←</button>
              <span style={{ fontSize: '1.5rem' }}>📝</span>
              <span style={{ color: 'white', fontWeight: 'bold' }}>Quiz - {selectedSurate.name}</span>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.2)', padding: '0.5rem 1rem', borderRadius: '8px', color: 'white' }}>⭐ {quizState.score}</div>
          </div>

          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem', gap: '2rem' }}>
            <div style={{ color: 'rgba(255,255,255,0.6)' }}>Question {quizState.currentQuestion + 1}/{selectedSurate.quiz.length}</div>
            <h3 style={{ color: 'white', fontSize: '1.5rem', textAlign: 'center' }}>{currentQ.question}</h3>

            {feedback && (
              <div style={{ padding: '1rem 2rem', borderRadius: '12px', background: feedback.type === 'correct' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)', color: feedback.type === 'correct' ? '#10b981' : '#ef4444', fontWeight: 'bold' }}>
                {feedback.message}
              </div>
            )}

            {!feedback && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', maxWidth: '500px', width: '100%' }}>
                {currentQ.options.map((option, i) => (
                  <button key={i} onClick={() => handleQuizAnswer(option)} style={{ padding: '1.5rem', borderRadius: '12px', border: '2px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.1)', color: 'white', fontSize: '1.1rem', cursor: 'pointer', transition: 'all 0.2s' }}
                    onMouseEnter={(e) => { e.target.style.borderColor = selectedSurate.color; e.target.style.transform = 'scale(1.02)'; }}
                    onMouseLeave={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.2)'; e.target.style.transform = 'scale(1)'; }}>
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    }

    // Mode Mémorisation
    if (viewMode === 'memorize') {
      return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.95)', zIndex: 9999, overflow: 'auto' }}>
          <audio ref={audioRef} />
          
          {/* Header */}
          <div style={{ padding: '1.5rem', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <button onClick={() => { stopPlayback(); setViewMode('read'); }} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', padding: '0.5rem 1rem', color: 'white', cursor: 'pointer', fontSize: '1.2rem' }}>←</button>
              <span style={{ fontSize: '2rem' }}>🧠</span>
              <div>
                <h2 style={{ margin: 0, color: 'white' }}>Mode Mémorisation</h2>
                <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)' }}>{selectedSurate.name} - {selectedSurate.nameAr}</p>
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', color: 'white', cursor: 'pointer' }}>✕</button>
          </div>

          <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
            {/* Paramètres de mémorisation */}
            <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '16px', padding: '1.5rem', marginBottom: '2rem' }}>
              <h3 style={{ color: 'white', marginTop: 0 }}>⚙️ Paramètres</h3>
              
              {/* Choix du récitateur */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ color: 'rgba(255,255,255,0.8)', display: 'block', marginBottom: '0.5rem' }}>🎙️ Récitateur :</label>
                <select 
                  value={selectedReciter.id} 
                  onChange={(e) => setSelectedReciter(RECITERS.find(r => r.id === parseInt(e.target.value)))}
                  style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: 'none', fontSize: '1rem' }}
                >
                  {RECITERS.map(r => (
                    <option key={r.id} value={r.id}>{r.name} - {r.nameAr}</option>
                  ))}
                </select>
              </div>

              {/* Plage de versets */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={{ color: 'rgba(255,255,255,0.8)', display: 'block', marginBottom: '0.5rem' }}>Du verset :</label>
                  <select 
                    value={memorizeSettings.startVerse}
                    onChange={(e) => setMemorizeSettings(prev => ({ ...prev, startVerse: parseInt(e.target.value) }))}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: 'none' }}
                  >
                    {selectedSurate.verses.map((v, i) => (
                      <option key={i} value={i}>Verset {v.number}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ color: 'rgba(255,255,255,0.8)', display: 'block', marginBottom: '0.5rem' }}>Au verset :</label>
                  <select 
                    value={memorizeSettings.endVerse}
                    onChange={(e) => setMemorizeSettings(prev => ({ ...prev, endVerse: parseInt(e.target.value) }))}
                    style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: 'none' }}
                  >
                    {selectedSurate.verses.map((v, i) => (
                      <option key={i} value={i} disabled={i < memorizeSettings.startVerse}>Verset {v.number}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Nombre de répétitions */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ color: 'rgba(255,255,255,0.8)', display: 'block', marginBottom: '0.5rem' }}>
                  🔁 Nombre de répétitions : {memorizeSettings.repeatCount}
                </label>
                <input 
                  type="range" 
                  min="1" 
                  max="10" 
                  value={memorizeSettings.repeatCount}
                  onChange={(e) => setMemorizeSettings(prev => ({ ...prev, repeatCount: parseInt(e.target.value) }))}
                  style={{ width: '100%' }}
                />
              </div>

              {/* Vitesse */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ color: 'rgba(255,255,255,0.8)', display: 'block', marginBottom: '0.5rem' }}>
                  ⏱️ Vitesse : {playbackSpeed}x
                </label>
                <input 
                  type="range" 
                  min="0.5" 
                  max="1.5" 
                  step="0.1"
                  value={playbackSpeed}
                  onChange={(e) => setPlaybackSpeed(parseFloat(e.target.value))}
                  style={{ width: '100%' }}
                />
              </div>

              {/* Boutons de contrôle */}
              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                {!isMemorizing ? (
                  <button
                    onClick={startMemorization}
                    disabled={isLoadingAudio}
                    style={{
                      padding: '1rem 2rem',
                      borderRadius: '12px',
                      border: 'none',
                      background: isLoadingAudio ? '#6b7280' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      cursor: isLoadingAudio ? 'wait' : 'pointer'
                    }}
                  >
                    {isLoadingAudio ? '⏳ Chargement...' : '▶️ Commencer la mémorisation'}
                  </button>
                ) : (
                  <button
                    onClick={stopPlayback}
                    style={{
                      padding: '1rem 2rem',
                      borderRadius: '12px',
                      border: 'none',
                      background: '#ef4444',
                      color: 'white',
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    ⏹️ Arrêter
                  </button>
                )}
              </div>

              {/* Progression */}
              {isMemorizing && (
                <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                  <div style={{ color: '#10b981', fontSize: '1.2rem', fontWeight: 'bold' }}>
                    Répétition {memorizeSettings.currentRepeat + 1} / {memorizeSettings.repeatCount}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.6)', marginTop: '0.5rem' }}>
                    Verset {currentVerseIndex + 1} sur {memorizeSettings.endVerse - memorizeSettings.startVerse + 1}
                  </div>
                </div>
              )}
            </div>

            {/* Versets avec surlignage */}
            <div>
              {selectedSurate.verses.map((verse, index) => {
                const isInRange = index >= memorizeSettings.startVerse && index <= memorizeSettings.endVerse;
                const isCurrent = index === currentVerseIndex;
                
                return (
                  <div
                    key={index}
                    onClick={() => !isMemorizing && playVerse(index)}
                    style={{
                      background: isCurrent 
                        ? 'rgba(16, 185, 129, 0.3)' 
                        : isInRange 
                          ? 'rgba(16, 185, 129, 0.1)' 
                          : 'rgba(255,255,255,0.05)',
                      borderRadius: '12px',
                      padding: '1.5rem',
                      marginBottom: '0.5rem',
                      cursor: isMemorizing ? 'default' : 'pointer',
                      transition: 'all 0.3s ease',
                      borderLeft: isCurrent ? '4px solid #10b981' : isInRange ? '4px solid rgba(16, 185, 129, 0.5)' : '4px solid transparent',
                      transform: isCurrent ? 'scale(1.02)' : 'scale(1)'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <span style={{
                        background: isInRange ? '#10b981' : 'rgba(255,255,255,0.2)',
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '0.9rem'
                      }}>
                        {verse.number}
                      </span>
                      {isCurrent && <span style={{ color: '#10b981', fontWeight: 'bold' }}>🔊 En cours...</span>}
                    </div>
                    
                    <div style={{
                      fontSize: isCurrent ? '2.2rem' : '1.8rem',
                      fontFamily: "'Amiri', 'Traditional Arabic', serif",
                      color: 'white',
                      textAlign: 'right',
                      direction: 'rtl',
                      lineHeight: 2,
                      marginBottom: '1rem',
                      transition: 'font-size 0.3s ease'
                    }}>
                      {verse.arabic}
                    </div>
                    
                    {showTransliteration && (
                      <div style={{ color: '#10b981', fontStyle: 'italic', marginBottom: '0.5rem' }}>
                        {verse.transliteration}
                      </div>
                    )}
                    
                    {showTranslation && (
                      <div style={{ color: 'rgba(255,255,255,0.8)' }}>
                        {verse.translation}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      );
    }

    // Mode Lecture normal
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.95)', zIndex: 9999, overflow: 'auto' }}>
        <audio ref={audioRef} />
        
        {/* Header */}
        <div style={{ padding: '1.5rem', background: selectedSurate.gradient, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <button onClick={() => { stopPlayback(); setSelectedSurate(null); }} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', padding: '0.5rem 1rem', color: 'white', cursor: 'pointer', fontSize: '1.2rem' }}>←</button>
            <span style={{ fontSize: '2.5rem' }}>{selectedSurate.icon}</span>
            <div>
              <h2 style={{ margin: 0, color: 'white' }}>Sourate {selectedSurate.name}</h2>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.9)', fontFamily: "'Amiri', serif", fontSize: '1.3rem' }}>
                سورة {selectedSurate.nameAr}
              </p>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={() => isPlaying ? stopPlayback() : playSurate()} disabled={isLoadingAudio} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', padding: '0.5rem 1rem', color: 'white', cursor: isLoadingAudio ? 'wait' : 'pointer' }}>
              {isLoadingAudio ? '⏳' : isPlaying ? '⏹️ Stop' : '▶️ Écouter'}
            </button>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', color: 'white', cursor: 'pointer' }}>✕</button>
          </div>
        </div>

        <div style={{ padding: '1.5rem', maxWidth: '800px', margin: '0 auto' }}>
          {/* Badges info */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <span style={{ background: 'rgba(255,255,255,0.1)', padding: '0.5rem 1rem', borderRadius: '20px', color: 'white', fontSize: '0.9rem' }}>
              📖 {selectedSurate.versesCount} versets
            </span>
            <span style={{ background: 'rgba(255,255,255,0.1)', padding: '0.5rem 1rem', borderRadius: '20px', color: 'white', fontSize: '0.9rem' }}>
              #{selectedSurate.number} dans le Coran
            </span>
          </div>

          {/* Choix du récitateur */}
          <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '12px', padding: '1rem', marginBottom: '1.5rem' }}>
            <label style={{ color: 'rgba(255,255,255,0.8)', display: 'block', marginBottom: '0.5rem' }}>🎙️ Récitateur :</label>
            <select 
              value={selectedReciter.id} 
              onChange={(e) => setSelectedReciter(RECITERS.find(r => r.id === parseInt(e.target.value)))}
              style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: 'none', fontSize: '1rem' }}
            >
              {RECITERS.map(r => (
                <option key={r.id} value={r.id}>{r.name} - {r.nameAr}</option>
              ))}
            </select>
          </div>

          {/* Introduction */}
          <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '16px', padding: '1.5rem', marginBottom: '1.5rem' }}>
            <p style={{ color: 'white', margin: 0, lineHeight: 1.6 }}>{selectedSurate.intro}</p>
          </div>

          {/* Options d'affichage */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <label style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={showTranslation} onChange={(e) => setShowTranslation(e.target.checked)} />
              🇫🇷 Traduction
            </label>
            <label style={{ color: 'white', display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={showTransliteration} onChange={(e) => setShowTransliteration(e.target.checked)} />
              🔤 Translittération
            </label>
          </div>

          {/* Versets */}
          <div style={{ marginBottom: '2rem' }}>
            {selectedSurate.verses.map((verse, index) => (
              <div
                key={index}
                onClick={() => playVerse(index)}
                style={{
                  background: currentVerseIndex === index ? `${selectedSurate.color}30` : index % 2 === 0 ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.1)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  marginBottom: '0.5rem',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  borderLeft: currentVerseIndex === index ? `4px solid ${selectedSurate.color}` : '4px solid transparent',
                  transform: currentVerseIndex === index ? 'scale(1.01)' : 'scale(1)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                  <span style={{
                    background: selectedSurate.gradient,
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.9rem'
                  }}>
                    {verse.number}
                  </span>
                  {currentVerseIndex === index && <span style={{ color: selectedSurate.color }}>🔊 En lecture...</span>}
                </div>
                
                <div style={{
                  fontSize: currentVerseIndex === index ? '2rem' : '1.8rem',
                  fontFamily: "'Amiri', 'Traditional Arabic', serif",
                  color: 'white',
                  textAlign: 'right',
                  direction: 'rtl',
                  lineHeight: 2,
                  marginBottom: '1rem',
                  transition: 'font-size 0.3s ease'
                }}>
                  {verse.arabic}
                </div>
                
                {showTransliteration && (
                  <div style={{ color: selectedSurate.color, fontStyle: 'italic', marginBottom: '0.5rem' }}>
                    {verse.transliteration}
                  </div>
                )}
                
                {showTranslation && (
                  <div style={{ color: 'rgba(255,255,255,0.8)' }}>{verse.translation}</div>
                )}
              </div>
            ))}
          </div>

          {/* Fun Facts */}
          <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '16px', padding: '1.5rem', marginBottom: '2rem' }}>
            <h3 style={{ color: 'white', marginTop: 0 }}>✨ Le savais-tu ?</h3>
            {selectedSurate.funFacts.map((fact, i) => (
              <div key={i} style={{ color: 'white', padding: '0.5rem 0', borderBottom: i < selectedSurate.funFacts.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none' }}>
                {fact}
              </div>
            ))}
          </div>

          {/* Boutons d'action */}
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => setViewMode('memorize')}
              style={{
                padding: '1rem 2rem',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              🧠 Mode Mémorisation
            </button>
            <button
              onClick={() => setViewMode('quiz')}
              style={{
                padding: '1rem 2rem',
                borderRadius: '12px',
                border: 'none',
                background: selectedSurate.gradient,
                color: 'white',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              📝 Quiz
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ==================== VUE PRINCIPALE - LISTE DES SOURATES ====================
  const beginnerSurates = suratesData.filter(s => s.level === 'beginner');
  const intermediateSurates = suratesData.filter(s => s.level === 'intermediate');

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.95)', zIndex: 9999, overflow: 'auto' }}>
      {/* Header */}
      <div style={{ padding: '1.5rem', background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', borderBottom: '2px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '2.5rem' }}>📖</span>
          <div>
            <h2 style={{ margin: 0, color: 'white', fontSize: '1.5rem' }}>Sourates Courtes</h2>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.7)', fontSize: '1rem' }}>قِصَارُ السُّوَر</p>
          </div>
        </div>
        <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', color: 'white', cursor: 'pointer', fontSize: '1.2rem' }}>✕</button>
      </div>

      <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto' }}>
        {/* Introduction */}
        <div style={{ background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)', borderRadius: '16px', padding: '1.5rem', marginBottom: '2rem', textAlign: 'center' }}>
          <p style={{ color: 'white', fontSize: '1.1rem', margin: 0 }}>
            🎧 Audio de récitateurs célèbres • 🧠 Mode mémorisation avec répétition • 📝 Quiz
          </p>
        </div>

        {/* Niveau Débutant */}
        <h3 style={{ color: '#10b981', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>🟢 Niveau Débutant</h3>
        <div style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
          {beginnerSurates.map((surate) => (
            <div key={surate.id} onClick={() => setSelectedSurate(surate)} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '1.5rem', cursor: 'pointer', transition: 'all 0.2s', border: '2px solid transparent', display: 'flex', alignItems: 'center', gap: '1.5rem' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${surate.color}20`; e.currentTarget.style.borderColor = surate.color; e.currentTarget.style.transform = 'translateX(5px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.transform = 'translateX(0)'; }}>
              <div style={{ background: surate.gradient, width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', flexShrink: 0 }}>{surate.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.25rem' }}>
                  <span style={{ color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}>{surate.name}</span>
                  <span style={{ color: surate.color, fontFamily: "'Amiri', serif" }}>{surate.nameAr}</span>
                </div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>{surate.meaning} • {surate.versesCount} versets</div>
              </div>
              <div style={{ color: surate.color, fontSize: '1.2rem' }}>→</div>
            </div>
          ))}
        </div>

        {/* Niveau Intermédiaire */}
        <h3 style={{ color: '#f59e0b', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>🟡 Niveau Intermédiaire</h3>
        <div style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
          {intermediateSurates.map((surate) => (
            <div key={surate.id} onClick={() => setSelectedSurate(surate)} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '12px', padding: '1.5rem', cursor: 'pointer', transition: 'all 0.2s', border: '2px solid transparent', display: 'flex', alignItems: 'center', gap: '1.5rem' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = `${surate.color}20`; e.currentTarget.style.borderColor = surate.color; e.currentTarget.style.transform = 'translateX(5px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.transform = 'translateX(0)'; }}>
              <div style={{ background: surate.gradient, width: '50px', height: '50px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', flexShrink: 0 }}>{surate.icon}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.25rem' }}>
                  <span style={{ color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}>{surate.name}</span>
                  <span style={{ color: surate.color, fontFamily: "'Amiri', serif" }}>{surate.nameAr}</span>
                </div>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>{surate.meaning} • {surate.versesCount} versets</div>
              </div>
              <div style={{ color: surate.color, fontSize: '1.2rem' }}>→</div>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '16px', padding: '1.5rem', textAlign: 'center' }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '3rem', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '2rem', color: '#10b981', fontWeight: 'bold' }}>{suratesData.length}</div>
              <div style={{ color: 'rgba(255,255,255,0.6)' }}>Sourates</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', color: '#f59e0b', fontWeight: 'bold' }}>{suratesData.reduce((acc, s) => acc + s.versesCount, 0)}</div>
              <div style={{ color: 'rgba(255,255,255,0.6)' }}>Versets</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', color: '#667eea', fontWeight: 'bold' }}>{RECITERS.length}</div>
              <div style={{ color: 'rgba(255,255,255,0.6)' }}>Récitateurs</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuratesHub;
